<script>
    import Button from './Button.svelte';
    import Modal from './Modal.svelte';

    import { paddle_purchase } from './utils/paddle';
    import { logEvent } from './utils/logs';
    import { goto } from '$app/navigation';
    import { discount_data } from './stores/globals';
    import { browser } from '$app/environment';
    import { format_price } from './utils/format_price';
    import { page } from '$app/stores';
    import { createEventDispatcher, onDestroy, onMount } from 'svelte';
    import { patch_single_product_prices } from './utils/patch_localized_prices';
    import { apply_discount, discount_applies } from './utils/discount';
    import { get_buy_path } from './utils/get_product_path';

    let session = $page.data.session;

    /** @type {Product} */
    export let product;

    export let cta = 'Buy Now';
    export let cta_free = 'Unlock Full Course';
    export let hidden = false;
    export let feature = 'generic';
    // Class of an element to embed the checkout in (see BuyPage.svelte).
    export let frame_target = '';
    export let loading = false;
    export let stopPropagation = false;
    export let no_price = false;
    export let no_badge = false;
    export let invert = false;
    export let price_key = 'price';
    /** @type {'default' | 'small'}*/
    export let style = 'default';

    export let discount_amount = 0;
    export let total_discount_percent = 0;

    // The real "before" price to strike through: a bundle's summed member
    // prices. Read from the product rather than passed in, so it localizes in
    // the same pass as the price itself (see patch_bundle) — a value captured
    // by the parent stays USD while the price turns into e.g. CAD.
    // 0 = no inherent discount (struck price hidden).
    const get_full_price = () =>
        price_key === 'price' && product && 'price_items' in product
            ? product.price_items?.price || 0
            : 0;

    let className = '';
    export { className as class };
    export let button_class = '';

    export const show = () => {
        handle_purchase();
    };

    let dispatch = createEventDispatcher();

    let show_error = false;

    let discount_applicable = false;

    /** @returns {Price} */
    const get_price_data = () => product?.[price_key] || null;

    const get_price_suffix = () => {
        const price_data = get_price_data();

        if (!price_data || price_data.price_type !== 'recurring') {
            return '';
        }

        const billing_cycle = price_data.billing_cycle;

        if (!billing_cycle) {
            return '';
        }

        const { interval, frequency } = billing_cycle;
        const cycle_count = frequency || 1;

        if (interval === 'day') {
            if (cycle_count % 7 === 0) {
                const weeks = cycle_count / 7;
                return ` / ${weeks === 1 ? 'week' : `${weeks} weeks`}`;
            }

            return ` / ${cycle_count === 1 ? 'day' : `${cycle_count} days`}`;
        }

        if (interval === 'month') {
            return ` / ${
                cycle_count === 1 ? 'month' : `${cycle_count} months`
            }`;
        }

        if (interval === 'year') {
            return ` / ${cycle_count === 1 ? 'year' : `${cycle_count} years`}`;
        }

        return '';
    };

    const format_price_display = (price_value, price_fx) =>
        `${format_price(price_value, price_fx)}${get_price_suffix()}`;

    export let price_value = get_price_data()?.price || 0;
    export let price_fx = get_price_data()?.price_fx || 'USD';
    let price_str = format_price_display(price_value, price_fx);

    let full_price_value = Math.max(get_full_price(), price_value);
    let full_price_str = format_price_display(full_price_value, price_fx);

    export let price_effective_value = price_value;
    let price_effective_str = format_price_display(
        price_effective_value,
        price_fx
    );
    let is_free = !price_value;

    const calculate_prices = () => {
        price_value = get_price_data()?.price || 0;
        price_fx = get_price_data()?.price_fx || 'USD';
        price_str = format_price_display(price_value, price_fx);

        full_price_value = Math.max(get_full_price(), price_value);
        full_price_str = format_price_display(full_price_value, price_fx);

        price_effective_value = price_value;
        price_effective_str = format_price_display(
            price_effective_value,
            price_fx
        );
        is_free = !price_value;
    };

    $: if ($discount_data && $discount_data.active) handle_discount_change();
    // Unconditional: a flat discount stops applying once prices localize to
    // another currency, and the percent must drop back with it.
    $: full_price_value, price_effective_value, calculate_total_discount();

    const handle_discount_change = () => {
        discount_applicable = discount_applies($discount_data, get_price_data());

        if (!discount_applicable) return;

        discount_amount = $discount_data.amount;

        price_effective_value = apply_discount(price_value, $discount_data);

        price_effective_str = format_price_display(
            price_effective_value,
            price_fx
        );
        is_free = price_effective_value <= 0;
    };

    handle_discount_change();

    const calculate_total_discount = () => {
        if (full_price_value > 0 && price_effective_value < full_price_value) {
            total_discount_percent = Math.round(
                ((full_price_value - price_effective_value) /
                    full_price_value) *
                    100
            );
        } else {
            total_discount_percent = 0;
        }
    };

    const success_cb = (event) => {
        document.removeEventListener('paddle_success', success_cb);
        document.removeEventListener('paddle_close', close_cb);

        const data = event.detail;

        dispatch('success');
        logEvent('purchase:success', {
            course: product.slug,
            product_slug: product.slug,
            product_type: product.type,
            feature
        });

        window.Paddle?.Checkout?.close();
        show_error = false;

        let redirect_url = '';

        switch (product.type) {
            case 'course':
                redirect_url = `/courses/${product.slug}/lessons`;
                break;
            case 'worksheet':
                redirect_url = `/worksheets/${product.slug}`;
                break;
            case 'bundle':
                redirect_url = `/bundles/${product.slug}`;
                break;
        }

        loading = false;
        goto(`/purchase-success?redirectTo=${redirect_url}`);
    };

    const close_cb = (event) => {
        document.removeEventListener('paddle_success', success_cb);
        document.removeEventListener('paddle_close', close_cb);
        const data = event.detail;
        console.log('paddle close', product.slug);
        loading = false;
        dispatch('close');
        logEvent('purchase:close', {
            course: product.slug,
            product_slug: product.slug,
            product_type: product.type,
            feature
        });
    };

    const handle_purchase = (e) => {
        if (stopPropagation) e.stopPropagation();

        loading = true;

        dispatch('show');
        logEvent('purchase:show', {
            course: product.slug,
            product_slug: product.slug,
            product_type: product.type,
            feature
        });

        document.addEventListener('paddle_success', success_cb);
        document.addEventListener('paddle_close', close_cb);

        // The promo code may have landed in this same tick (BuyPage opens
        // right after the root layout's lookup), before the reactive update.
        handle_discount_change();

        paddle_purchase({
            price_id: get_price_data()?.price_id,
            discount_id: discount_applicable
                ? $discount_data?.discount_id || null
                : null,
            email: session?.user?.email || null,
            feature,
            frame_target: frame_target || null
        });
    };

    // An inline checkout (BuyPage) goes away with this component; an overlay
    // can outlive it (a button in a closed modal) and still needs the callbacks.
    onDestroy(() => {
        if (!browser || !frame_target) return;
        document.removeEventListener('paddle_success', success_cb);
        document.removeEventListener('paddle_close', close_cb);
    });

    onMount(() => {
        patch_single_product_prices(product)
            .then((p) => {
                product = p;
                calculate_prices();
                handle_discount_change();
            })
            .catch((error) => console.warn('Failed to update prices:', error));
    });
</script>

<!-- svelte-ignore a11y-click-events-have-key-events -->
<!-- svelte-ignore a11y-no-static-element-interactions -->
{#if !hidden}
    <!-- A real link for agents and no-JS visitors; clicks open the overlay. -->
    {@const buy_path = price_key === 'price' ? get_buy_path(product) : ''}

    <slot
        name="before"
        {cta}
        {cta_free}
        {is_free}
        {price_str}
        {price_fx}
        {price_value}
        {price_effective_str}
        {price_effective_value}
        {total_discount_percent}
        {discount_amount}
    />

    <div
        on:click={handle_purchase}
        class="relative {style === 'small'
            ? 'inline-flex items-center gap-2'
            : ''} {className}"
    >
        <slot
            {cta}
            {cta_free}
            {is_free}
            {price_str}
            {price_effective_str}
            {full_price_str}
            {handle_purchase}
        >
            {#if style == 'small'}
                <Button
                    primary
                    class="whitespace-nowrap {button_class || ''}"
                    route={buy_path}
                    preventDefault
                    loading={!browser || loading}
                    loading_spinner={browser}
                    {invert}
                >
                    {is_free ? cta_free : cta}
                </Button>

                {#if !no_price}
                    {#if full_price_value > price_value && !is_free}
                        <s>{full_price_str}</s>
                        {price_effective_value !== price_value
                            ? price_effective_str
                            : price_str}
                    {:else if price_effective_value !== price_value && !is_free}
                        <s>{price_str}</s> {price_effective_str}
                    {:else if !is_free}
                        {price_str}
                    {/if}
                {/if}
            {:else}
                <Button
                    primary
                    xl
                    class="whitespace-nowrap {button_class || ''}"
                    block
                    route={buy_path}
                    preventDefault
                    loading={!browser || loading}
                    loading_spinner={browser}
                    {invert}
                >
                    {is_free ? cta_free : cta}

                    <svelte:fragment slot="small">
                        {#if is_free}
                            Redeem your gift
                        {:else if full_price_value > price_value}
                            <s>{full_price_str}</s>
                            {price_effective_value !== price_value
                                ? price_effective_str
                                : price_str}
                            {#if 'lessons_count' in product && product.lessons_count}
                                for {product.lessons_count} lessons
                            {/if}
                        {:else if price_effective_value !== price_value && !is_free}
                            <s>{price_str}</s>
                            {price_effective_str}
                            {#if 'lessons_count' in product && product.lessons_count}
                                for {product.lessons_count} lessons
                            {/if}
                        {:else}
                            {price_str}
                            {#if 'lessons_count' in product && product.lessons_count}
                                for {product.lessons_count} lessons
                            {/if}
                        {/if}
                    </svelte:fragment>
                </Button>
            {/if}
        </slot>

        {#if !no_badge && style == 'default' && (discount_applicable || full_price_value > price_value) && price_value}
            <div
                class="text-center text-sm font-bold
                px-2 py-1
                bg-purple-600 text-purple-50 rounded-full
                shadow-md
                cursor-pointer

                {style === 'default'
                    ? 'absolute -top-4 -right-4 '
                    : 'inline-block ml-2'}
            "
            >
                {#if is_free}
                    Free
                {:else}
                    &minus;{total_discount_percent}%
                {/if}
            </div>
        {/if}
    </div>

    <slot
        name="after"
        {cta}
        {cta_free}
        {is_free}
        {price_str}
        {price_value}
        {price_effective_str}
        {price_effective_value}
        {total_discount_percent}
        {discount_amount}
    />
{/if}

{#if show_error}
    <Modal on:close={() => (show_error = false)}>
        <h1>Something went wrong</h1>
        <p>We could not process your purchase automatically</p>
        <p>
            Please contact us via <a href="mailto:support@critikid.com"
                >support@critikid.com</a
            > to restore your purchase
        </p>
    </Modal>
{/if}
